import React, { useState } from 'react';
import { LayoutDashboard, Heart, FileText, Video, Calculator, Download, Upload, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Link } from 'react-router-dom';
import { MessageModal } from '../../components/modals/MessageModal';
export function PoaLegalPage() {
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    type: 'info' as 'success' | 'error' | 'info'
  });
  const handleViewDocument = (docName: string) => {
    setModalContent({
      title: 'Document Viewer',
      message: `Opening ${docName}. In production, this would open a PDF viewer or download the document.`,
      type: 'info'
    });
    setIsMessageModalOpen(true);
  };
  const handleUploadDocument = (docType: string) => {
    setModalContent({
      title: 'Upload Document',
      message: `Document upload for ${docType} is coming soon. You will be able to upload signed copies directly through the platform.`,
      type: 'info'
    });
    setIsMessageModalOpen(true);
  };
  const handleFindLawyer = () => {
    setModalContent({
      title: 'Find a Lawyer',
      message: 'Our verified lawyer network is coming soon. We will connect you with property lawyers specializing in NRI transactions in your target city.',
      type: 'info'
    });
    setIsMessageModalOpen(true);
  };
  const navItems = [{
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/dashboard/nri-buyer'
  }, {
    label: 'Shortlisted',
    icon: Heart,
    path: '/buyer/shortlisted'
  }, {
    label: 'POA & Legal',
    icon: FileText,
    path: '/nri/poa-legal'
  }, {
    label: 'Virtual Tours',
    icon: Video,
    path: '/nri/virtual-tours'
  }, {
    label: 'Tax & Forex',
    icon: Calculator,
    path: '/nri/tax-forex'
  }];
  return <DashboardLayout title="NRI Services" personaLabel="NRI MODE" personaColor="bg-purple-100 text-purple-800 border-purple-200" navItems={navItems} userInitials="NR" data-id="element-4912">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8" data-id="element-4913">
        <div data-id="element-4914">
          <h1 className="text-2xl font-bold mb-1" data-id="element-4915">Power of Attorney & Legal</h1>
          <p className="text-gray-500 font-mono text-sm" data-id="element-4916">
            Manage POA documents and legal compliance.
          </p>
        </div>
        <button onClick={handleFindLawyer} className="px-4 py-2 bg-[#1f2933] text-white text-sm font-bold hover:bg-gray-800" data-id="element-4917">
          Find a Lawyer
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4918">
        {/* POA Documents */}
        <div className="lg:col-span-2 space-y-6" data-id="element-4919">
          <div className="bg-white border border-gray-300 p-6" data-id="element-4920">
            <h2 className="font-bold text-lg mb-6" data-id="element-4921">POA Documents</h2>
            <div className="space-y-4" data-id="element-4922">
              {[{
              name: 'General Power of Attorney (Draft)',
              status: 'Ready',
              date: 'Oct 15, 2024',
              type: 'draft'
            }, {
              name: 'Special POA - Property Transaction',
              status: 'Pending Signature',
              date: 'Oct 12, 2024',
              type: 'pending'
            }, {
              name: 'Notarized POA Copy',
              status: 'Awaiting Upload',
              date: '-',
              type: 'upload'
            }].map((doc, i) => <div key={i} className="flex items-center justify-between p-4 border border-gray-200 hover:border-gray-400 transition-colors" data-id="element-4923">
                  <div className="flex items-center gap-3" data-id="element-4924">
                    <FileText className="w-5 h-5 text-gray-400" data-id="element-4925" />
                    <div data-id="element-4926">
                      <h3 className="font-bold text-sm" data-id="element-4927">{doc.name}</h3>
                      <p className="text-xs text-gray-500" data-id="element-4928">
                        {doc.status} • {doc.date}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2" data-id="element-4929">
                    {doc.type === 'draft' && <>
                        <button onClick={() => handleViewDocument(doc.name)} className="px-3 py-1.5 border border-gray-300 text-xs font-bold hover:bg-gray-50 flex items-center gap-1" data-id="element-4930">
                          <ExternalLink className="w-3 h-3" data-id="element-4931" /> View
                        </button>
                        <button className="px-3 py-1.5 bg-[#1f2933] text-white text-xs font-bold hover:bg-gray-800 flex items-center gap-1" data-id="element-4932">
                          <Download className="w-3 h-3" data-id="element-4933" /> Download
                        </button>
                      </>}
                    {doc.type === 'pending' && <button onClick={() => handleViewDocument(doc.name)} className="px-3 py-1.5 border border-gray-300 text-xs font-bold hover:bg-gray-50 flex items-center gap-1" data-id="element-4934">
                        <ExternalLink className="w-3 h-3" data-id="element-4935" /> View
                      </button>}
                    {doc.type === 'upload' && <button onClick={() => handleUploadDocument(doc.name)} className="px-3 py-1.5 bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 flex items-center gap-1" data-id="element-4936">
                        <Upload className="w-3 h-3" data-id="element-4937" /> Upload Signed Copy
                      </button>}
                  </div>
                </div>)}
            </div>
          </div>

          <div className="bg-white border border-gray-300 p-6" data-id="element-4938">
            <h2 className="font-bold text-lg mb-6" data-id="element-4939">Legal Guides for NRIs</h2>
            <div className="space-y-3" data-id="element-4940">
              {['POA Requirements for NRI Property Transactions', 'Apostille Process for Indian Documents', 'FEMA Compliance Checklist', 'Tax Implications for NRI Property Sales'].map((guide, i) => <Link key={i} to="/legal-guides" className="flex items-center justify-between p-3 border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-colors group" data-id="element-4941">
                  <span className="text-sm font-medium group-hover:text-blue-600" data-id="element-4942">
                    {guide}
                  </span>
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-600" data-id="element-4943" />
                </Link>)}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-4944">
          <div className="bg-green-50 border border-green-200 p-6" data-id="element-4945">
            <h3 className="font-bold text-green-900 mb-4 flex items-center gap-2" data-id="element-4946">
              <CheckCircle className="w-5 h-5" data-id="element-4947" /> POA Checklist
            </h3>
            <div className="space-y-3" data-id="element-4948">
              {[{
              label: 'Draft POA prepared',
              done: true
            }, {
              label: 'Notarization completed',
              done: true
            }, {
              label: 'Apostille obtained',
              done: false
            }, {
              label: 'Registered in India',
              done: false
            }].map((item, i) => <div key={i} className="flex items-center gap-2" data-id="element-4949">
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${item.done ? 'bg-green-600 border-green-600' : 'border-gray-300'}`} data-id="element-4950">
                    {item.done && <CheckCircle className="w-3 h-3 text-white" data-id="element-4951" />}
                  </div>
                  <span className={`text-sm ${item.done ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-4952">
                    {item.label}
                  </span>
                </div>)}
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-6" data-id="element-4953">
            <h3 className="font-bold text-yellow-900 mb-3 flex items-center gap-2" data-id="element-4954">
              <AlertCircle className="w-5 h-5" data-id="element-4955" /> Important Notes
            </h3>
            <ul className="space-y-2 text-xs text-yellow-800" data-id="element-4956">
              <li data-id="element-4957">• POA must be notarized in your country of residence</li>
              <li data-id="element-4958">• Apostille required for non-Hague countries</li>
              <li data-id="element-4959">• Registration in India within 4 months</li>
              <li data-id="element-4960">• Keep original POA safely</li>
            </ul>
          </div>
        </div>
      </div>

      <MessageModal isOpen={isMessageModalOpen} onClose={() => setIsMessageModalOpen(false)} title={modalContent.title} message={modalContent.message} type={modalContent.type} data-id="element-4961" />
    </DashboardLayout>;
}